import axios, { AxiosInstance } from 'axios';

const isCheckoutPath = () => {
  const p = window.location.pathname;
  return p.startsWith('/checkout') || p.startsWith('/payment');
};

const isLoginPath = () => window.location.pathname.startsWith('/login');
const isPublicCardStatusPath = () => window.location.pathname.startsWith('/card-status');



export class HttpClient {
  private static instance: AxiosInstance | null = null;
  private static redirecting = false;
  private static bootGraceUntil = 0;

  public static enterBootGrace(periodMs = 2000) {
    HttpClient.bootGraceUntil = Date.now() + periodMs;
  }

  private static hasAuthNow(configHeaders?: any): boolean {
    const headerAuth =
      !!(configHeaders && (configHeaders as any)['Authorization']);
    const defaultAuth =
      !!HttpClient.instance?.defaults?.headers?.common?.['Authorization'];
    const lsAuth =
      !!localStorage.getItem('token') || !!localStorage.getItem('auth_token');

    return headerAuth || defaultAuth || lsAuth;
  }

private static installInterceptors(instance: AxiosInstance) {
    instance.interceptors.request.use((config) => {
      const url = config.url || '';

      const hasToken = !!localStorage.getItem('token') || !!localStorage.getItem('auth_token');

      if (/\/self(?:$|\?|#)/.test(url) && !hasToken) {
        return Promise.reject(new axios.Cancel('skip /self without token'));
      }

      // (opcjonalnie) jeśli kiedyś wróci ping:
      if (/\/ping(?:$|\?|#)/.test(url) && !hasToken) {
        return Promise.reject(new axios.Cancel('skip /ping without token'));
      }

      return config;
    });

    instance.interceptors.response.use(
      (resp) => resp,
      (error) => {
        if (axios.isCancel(error)) return Promise.reject(error);

        const status = error?.response?.status;
        const headers = error?.response?.headers || {};
        const reqUrl = String(error?.config?.url || '');
        const onLogin = isLoginPath();
        const onPublic = isPublicCardStatusPath();

        if (status === 426 || headers['x-app-update'] === 'required') {
          if (!onLogin && !isCheckoutPath()) window.location.reload();
          return new Promise(() => {});
        }

        if (status === 401 && onPublic) {
          return Promise.reject(error);
        }

        if (
          status === 401 &&
          /\/self(?:$|\?|#)/.test(reqUrl) &&
          Date.now() < HttpClient.bootGraceUntil
        ) {
          return Promise.reject(error);
        }

        if (status === 401) {
          const isAuthRoute = reqUrl.startsWith('/login') || reqUrl.startsWith('/auth');
          if (isAuthRoute || onLogin) return Promise.reject(error);

          if (HttpClient.redirecting) return Promise.reject(error);
          HttpClient.redirecting = true;

          try {
            const inst = HttpClient.getInstance();
            delete inst.defaults.headers.common['Authorization'];
            localStorage.removeItem('auth_token');
            localStorage.removeItem('token');
            sessionStorage.setItem('loggedOutReason', 'expired');
          } catch {}

          const url = new URL(window.location.href);
          const alreadyExpired = url.searchParams.get('expired') === '1';
          const hasNextParam = !!url.searchParams.get('next');

          if (isLoginPath() || alreadyExpired) {
            window.location.href = '/login?expired=1';
          } else {
            const next = encodeURIComponent(url.pathname + url.search);
            const target = hasNextParam ? '/login?expired=1' : `/login?expired=1&next=${next}`;
            window.location.href = target;
          }

          return Promise.reject(error);
        }

        return Promise.reject(error);
      },
    );
  }
  private static buildInstance() {
    const baseURL = process.env.REACT_APP_API_URL;

    const instance = axios.create({
      baseURL,
      headers: { 'Access-Control-Allow-Origin': '*' },
    });

    const bootToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (bootToken && !instance.defaults.headers.common['Authorization']) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${bootToken}`;
    }

    HttpClient.installInterceptors(instance);
    return instance;
  }

  public static getInstance(): AxiosInstance {
    if (!HttpClient.instance) {
      HttpClient.instance = HttpClient.buildInstance();
    }
    return HttpClient.instance;
  }

  public static setToken(token: string): void {
    const inst = HttpClient.getInstance();
    inst.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('token', token);
  }

  public static clearToken(): void {
    const inst = HttpClient.getInstance();
    delete inst.defaults.headers.common['Authorization'];
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
  }
}